// ─── importParamsFromFile ──────────────────────────────────────────────────
// Read a previously-exported output file (.md / .doc / .docx / .txt / .json)
// and recover the wire-shape `params` so Step 1 can be re-populated.
//
// Markdown/Word/PDF exports embed an HTML comment marker via
// buildInputJsonComment() in useHistory.js:
//     <!-- ESC_INPUT_DATA_V1: { version, entryId, timestamp, mode, params } -->
// JSON files may be:
//   1. The marker payload      { version: 1, params: { ... }, ... }
//   2. A raw wire-shape params { activeMode, content_params, ... }
//   3. A brief envelope        { platform, content_format, content_params, ... }
//
// Returns form-state ready for `dispatch({ type: 'SET_BATCH', payload })`.

import { paramsToFormState } from './paramsToFormState.js';
import { briefEnvelopeToFormState } from './briefEnvelopeToFormState.js';

const MARKER_RE = /<!--\s*ESC_INPUT_DATA_V1:\s*([\s\S]*?)\s*-->/;

const TEXT_EXTENSIONS = ['.md', '.txt', '.doc', '.docx', '.html', '.htm'];
const JSON_EXTENSIONS = ['.json'];

export class ImportParamsError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ImportParamsError';
  }
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new ImportParamsError('Errore lettura file'));
    reader.readAsText(file);
  });
}

// Try the three shapes a JSON file may carry. Returns wire-shape params, or null.
function paramsFromJsonObject(obj) {
  if (!obj || typeof obj !== 'object') return null;
  // Shape 1: marker payload — has .params
  if (obj.params && typeof obj.params === 'object') return obj.params;
  // Shape 2: wire-shape params — has activeMode + content_params
  if (obj.activeMode && obj.content_params !== undefined) return obj;
  // Shape 3: brief envelope — has platform + content_format
  if (obj.platform && obj.content_format) return { __brief: true, brief: obj };
  return null;
}

export function extractParamsFromText(text) {
  if (!text || typeof text !== 'string') return null;
  const m = text.match(MARKER_RE);
  if (!m) return null;
  try {
    const data = JSON.parse(m[1]);
    return paramsFromJsonObject(data);
  } catch {
    return null;
  }
}

export function extractParamsFromJson(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  return paramsFromJsonObject(parsed);
}

// Public entry point — accepts a File (browser) and returns form-state.
export async function importParamsFromFile(file) {
  if (!file) throw new ImportParamsError('Nessun file selezionato');

  const name = file.name || '';
  const ext = name.slice(name.lastIndexOf('.')).toLowerCase();

  if (ext === '.pdf') {
    throw new ImportParamsError(
      'I PDF non sono importabili: l\'export PDF rasterizza il contenuto e perde i metadati. Usa il file .md o .docx esportato dalla stessa generazione.',
    );
  }

  const isText = TEXT_EXTENSIONS.includes(ext);
  const isJson = JSON_EXTENSIONS.includes(ext);
  if (!isText && !isJson) {
    throw new ImportParamsError(
      `Tipo di file non supportato (${ext || 'sconosciuto'}). Formati ammessi: ${[...TEXT_EXTENSIONS, ...JSON_EXTENSIONS].join(', ')}`,
    );
  }

  const text = await readFileAsText(file);

  let extracted = null;
  if (isJson) {
    extracted = extractParamsFromJson(text);
    if (!extracted) extracted = extractParamsFromText(text); // fall back: marker inside .json
  } else {
    extracted = extractParamsFromText(text);
    if (!extracted) extracted = extractParamsFromJson(text); // fall back: raw JSON saved as .txt
  }

  if (!extracted) {
    throw new ImportParamsError(
      'Nessun parametro riconoscibile nel file. Importa un file .md / .docx esportato dall\'app o un .json compatibile.',
    );
  }

  // Brief envelope path
  if (extracted.__brief) {
    return briefEnvelopeToFormState(extracted.brief);
  }

  // Wire-shape params path
  return paramsToFormState(extracted);
}
